import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Eye, Send, Users, Check, X, Filter, 
  Phone, Calendar, CheckCircle2, XCircle, User, Plus, Edit2, Trash2, Upload, Loader2,
  ChevronLeft, ChevronRight, Megaphone
} from 'lucide-react';
import api, { createContact, updateContact, deleteContact, getUsers, getSectors, importContacts } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const statusLabels = {
  new: { label: 'Novo', color: 'bg-blue-100 text-blue-700' },
  contacted: { label: 'Contatado', color: 'bg-yellow-100 text-yellow-700' },
  qualified: { label: 'Qualificado', color: 'bg-purple-100 text-purple-700' },
  converted: { label: 'Convertido', color: 'bg-green-100 text-green-700' },
  lost: { label: 'Perdido', color: 'bg-red-100 text-red-700' }
};

const sourceLabels = {
  manual: 'Manual',
  website: 'Website',
  whatsapp: 'WhatsApp',
  campaign: 'Campanha',
  import: 'Importação'
};

const ITEMS_PER_PAGE = 10;

function Contacts() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filterOptIn, setFilterOptIn] = useState('all');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [importing, setImporting] = useState(false);
  const fileInputRef = React.useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser(payload);
      } catch (err) {
        console.error('Erro ao carregar usuário:', err);
      }
    }
  }, []);
  const isAttendant = currentUser?.role === 'attendant';

  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    profile_name: '', wa_id: '', email: '',
    source: 'manual', status: 'new', assigned_to: '', sector_id: ''
  });

  // ⭐ CORRIGIDO: agora busca só a página atual do servidor (antes baixava
  // tudo de uma vez e cortava no navegador — lento com muitos contatos)
  const [totalContacts, setTotalContacts] = useState(0);
  const [contactStats, setContactStats] = useState({ total: 0, optIn: 0, optOut: 0 });

  const loadStats = async () => {
    try {
      const res = await api.get('/api/contacts/stats');
      setContactStats(res.data);
    } catch (err) {
      console.error('Erro ao buscar totais de contatos:', err);
    }
  };
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadContacts = async (searchTerm, pageNum, optIn) => {
    setLoading(true);
    try {
      const params = { page: pageNum || 1, limit: ITEMS_PER_PAGE };
      if (searchTerm) params.search = searchTerm;
      if (optIn) params.opt_in = optIn;
      const res = await api.get('/api/contacts', { params });
      if (isAttendant) {
        setContacts(Array.isArray(res.data) ? res.data : []); // atendente: array puro
      } else {
        setContacts(res.data.contacts || []); // admin/gerente: { contacts, total }
        setTotalContacts(res.data.total || 0);
      }
    } catch (err) {
      console.error('Erro ao buscar contatos', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser === null) return; // aguarda saber o role antes de decidir como carregar
    loadFilters();
    loadStats();
  }, [currentUser]);

  // ⭐ NOVO: busca do servidor de novo sempre que página, busca ou filtro mudam
  useEffect(() => {
    if (currentUser === null) return;
    if (isAttendant && !debouncedSearch.trim()) {
      setContacts([]);
      setLoading(false); // atendente sem busca digitada: não carrega nada
      return;
    }
    loadContacts(debouncedSearch, currentPage, filterOptIn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, currentPage, debouncedSearch, filterOptIn]);

  const loadFilters = async () => {
    try {
      const [usersRes, sectorsRes] = await Promise.all([getUsers(), getSectors()]);
      setUsers(usersRes.data);
      setSectors(sectorsRes.data);
    } catch (err) {
      console.error('Erro ao buscar usuários/setores', err);
    }
  };

  // ⭐ CORRIGIDO: servidor já devolve só a página certa, filtrada — sem cortar de novo aqui
  const filtered = contacts;
  const paginatedContacts = contacts;
  const totalPages = Math.max(1, Math.ceil(totalContacts / ITEMS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterOptIn]);

  const toggleContact = (id) => {
    setSelectedContacts(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(paginatedContacts.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    if (paginatedContacts.length > 0 && selectedContacts.length === paginatedContacts.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedContacts, paginatedContacts]);

  const handleSendToBulk = () => {
    if (selectedContacts.length === 0) {
      alert(t('contacts.selectAtLeastOne'));
      return;
    }

    const selectedData = contacts
      .filter(c => selectedContacts.includes(c.id))
      .map(c => ({
        id: c.id,
        wa_id: c.wa_id,
        profile_name: c.profile_name
      }));

    navigate('/bulk-messaging', { 
      state: { preSelectedContacts: selectedData } 
    });
  };

  const handleViewContact = (contact) => {
    alert(`
👤 ${t('contacts.name')}: ${contact.profile_name || t('contacts.notInformed')}
📞 ${t('contacts.phone')}: ${contact.wa_id}
✅ Opt-in: ${contact.opt_in ? t('common.yes') : t('common.no')}
📅 ${t('contacts.createdIn')}: ${new Date(contact.created_at).toLocaleDateString('pt-BR')}
🏷️ Status: ${statusLabels[contact.status]?.label || contact.status || '-'}
📍 Origem: ${sourceLabels[contact.source] || contact.source || '-'}
👤 Responsável: ${contact.assigned_to_name || 'Sem responsável'}
🏢 Setor: ${contact.sector_name || 'Sem setor'}
    `);
  };

  const openNewContactModal = () => {
    setEditingContact(null);
    setForm({ profile_name: '', wa_id: '', email: '', source: 'manual', status: 'new', assigned_to: '', sector_id: '' });
    setShowModal(true);
  };

  const openEditContactModal = (contact) => {
    setEditingContact(contact);
    setForm({
      profile_name: contact.profile_name || '',
      wa_id: contact.wa_id || '',
      email: contact.email || '',
      source: contact.source || 'manual',
      status: contact.status || 'new',
      assigned_to: contact.assigned_to || '',
      sector_id: contact.sector_id || ''
    });
    setShowModal(true);
  };

  const handleSaveContact = async (e) => {
    e.preventDefault();
    if (!form.profile_name.trim() || !form.wa_id.trim()) {
      alert('Nome e telefone são obrigatórios');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        profile_name: form.profile_name,
        email: form.email,
        source: form.source,
        status: form.status,
        assigned_to: form.assigned_to || null,
        sector_id: form.sector_id || null
      };

      if (editingContact) {
        await updateContact(editingContact.id, payload);
      } else {
        const cleanWaId = form.wa_id.replace(/\D/g, '');
        await createContact({
          ...payload,
          wa_id: cleanWaId,
          phone: cleanWaId
        });
      }
      setShowModal(false);
      loadContacts(debouncedSearch, currentPage, filterOptIn);
      loadStats();
    } catch (err) {
      alert('Erro ao salvar contato: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Excluir o contato "${contact.profile_name || contact.wa_id}"?`)) return;
    try {
      await deleteContact(contact.id);
      loadContacts(debouncedSearch, currentPage, filterOptIn);
      loadStats();
    } catch (err) {
      alert('Erro ao excluir contato: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleImportClick = () => {
    if (importing) return;
    fileInputRef.current?.click();
  };

  // ⭐ NOVO: modal de exportação com filtros (estilo Meta Business Manager)
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState('filtered'); // 'all' | 'filtered'
  const [exportFilters, setExportFilters] = useState([
    { key: 'f1', field: 'origem', value: 'ctwa' }
  ]);
  const [exporting, setExporting] = useState(false);

  const openExportModal = () => {
    setExportMode('filtered');
    setExportFilters([{ key: 'f1', field: 'origem', value: 'ctwa' }]);
    setShowExportModal(true);
  };
  const addExportFilter = () => {
    setExportFilters(prev => [...prev, { key: `f${Date.now()}`, field: 'origem', value: 'ctwa' }]);
  };
  const removeExportFilter = (key) => {
    setExportFilters(prev => prev.filter(f => f.key !== key));
  };
  const updateExportFilter = (key, changes) => {
    setExportFilters(prev => prev.map(f => f.key === key ? { ...f, ...changes } : f));
  };
  const defaultValueForField = (field) => {
    if (field === 'origem') return 'ctwa';
    if (field === 'sector_id') return sectors[0]?.id || '';
    if (field === 'status') return 'new';
    return '';
  };

  const handleApplyExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set('mode', exportMode);
      if (exportMode === 'filtered') {
        params.set('filters', JSON.stringify(exportFilters.map(({ field, value }) => ({ field, value }))));
      }
      const res = await api.get(`/api/contacts/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contatos-exportados.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch (err) {
      alert('Erro ao exportar contatos: ' + (err.response?.data?.error || err.message));
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const res = await importContacts(file);
      const { total, imported, skipped, invalid, errors } = res.data;
      let msg = `📊 Importação concluída:\n\n✅ Importados: ${imported}\n⏭️ Já existiam (pulados): ${skipped}\n⚠️ Inválidos: ${invalid}\n📄 Total de linhas: ${total}`;
      if (errors && errors.length > 0) {
        msg += `\n\nAlguns problemas encontrados:\n${errors.join('\n')}`;
      }
      alert(msg);
      loadContacts(debouncedSearch, currentPage, filterOptIn);
      loadStats();
    } catch (err) {
      alert('Erro ao importar planilha: ' + (err.response?.data?.error || err.message));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ⭐ CORRIGIDO: totais vêm do servidor agora, não dá mais pra somar só o
  // que está na tela (a lista só mostra uma página por vez)
  const stats = {
    total: contactStats.total,
    optIn: contactStats.optIn,
    optOut: contactStats.optOut,
    filtered: filtered.length,
    selected: selectedContacts.length
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">{t('contacts.title')}</h1>
          <p className="text-gray-500 dark:text-slate-400">{t('contacts.subtitle')}</p>
        </div>

        <div className="flex items-center gap-3">
          {!isAttendant && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImportFile}
                accept=".xlsx,.xls,.csv"
                className="hidden"
              />
              <button
                onClick={handleImportClick}
                disabled={importing}
                className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-all disabled:opacity-50"
              >
                {importing ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                {importing ? 'Importando...' : 'Importar Planilha'}
              </button>
              {/* ⭐ NOVO: exporta só os leads que vieram de anúncio patrocinado (CTWA) */}
              <button
                onClick={openExportModal}
                className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-all"
                title="Exporta contatos com filtros, incluindo leads de anúncio patrocinado (CTWA)"
              >
                <Megaphone size={18} />
                Exportar Leads de Anúncios
              </button>
            </>
          )}

          <button
            onClick={openNewContactModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 shadow-lg shadow-green-100 dark:shadow-none transition-all"
          >
            <Plus size={18} />
            Novo Contato
          </button>

          <div className="flex gap-3">
            <div className="bg-white dark:bg-slate-800 px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700">
              <p className="text-xs text-gray-500 dark:text-slate-400">{t('contacts.total')}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-slate-100">{stats.total}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-xl border border-green-200 dark:border-green-800">
              <p className="text-xs text-green-700 dark:text-green-400">Opt-in</p>
              <p className="text-lg font-bold text-green-900 dark:text-green-300">{stats.optIn}</p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 px-4 py-2 rounded-xl border border-red-200 dark:border-red-800">
              {/* ⭐ CORRIGIDO: era "Opt-out", rótulo enganoso — esse número junta
                  quem revogou de verdade com quem nunca teve opt-in registrado
                  (a grande maioria); "Sem Opt-in" descreve o que é de fato */}
              <p className="text-xs text-red-700 dark:text-red-400">Sem Opt-in</p>
              <p className="text-lg font-bold text-red-900 dark:text-red-300">{stats.optOut}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={18} />
            <input
              type="text"
              placeholder={t('contacts.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={16} />
            <select
              value={filterOptIn}
              onChange={e => setFilterOptIn(e.target.value)}
              className="pl-10 pr-8 py-2.5 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none text-gray-900 dark:text-slate-100 appearance-none cursor-pointer"
            >
              <option value="all">{t('contacts.all')}</option>
              <option value="yes">{t('contacts.optInOnly')}</option>
              <option value="no">{t('contacts.optOutOnly')}</option>
            </select>
          </div>
        </div>

        {!isAttendant && selectedContacts.length > 0 && (
          <div className="mt-4 flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                {selectedContacts.length}
              </div>
              <p className="text-sm font-medium text-green-900 dark:text-green-300">
                {t('contacts.selectedContacts', { count: selectedContacts.length })}
              </p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedContacts([])}
                className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                <X size={16} />
                {t('contacts.clear')}
              </button>
              
              <button
                onClick={handleSendToBulk}
                className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg text-sm font-bold hover:from-green-700 hover:to-green-800 shadow-lg shadow-green-200 dark:shadow-none transition-all"
              >
                <Send size={16} />
                {t('contacts.sendToBulk')}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
              <tr>
                {!isAttendant && (
                  <th className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500 cursor-pointer"
                    />
                  </th>
                )}
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('contacts.contact')}
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Origem
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Responsável
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Campanha
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Opt-in
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('contacts.createdAt')}
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider text-center">
                  {t('contacts.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                    <p className="text-gray-500 dark:text-slate-400 text-sm">{t('contacts.loadingContacts')}</p>
                  </td>
                </tr>
              ) : paginatedContacts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <Users size={48} className="mx-auto text-gray-300 dark:text-slate-600 mb-3" />
                    <p className="text-gray-500 dark:text-slate-400 font-medium">{t('contacts.noResults')}</p>
                    <p className="text-gray-400 dark:text-slate-500 text-sm mt-1">
                      {search || filterOptIn !== 'all' ? t('contacts.tryAdjustFilters') : t('contacts.contactsWillAppear')}
                    </p>
                  </td>
                </tr>
              ) : (
                paginatedContacts.map(contact => {
                  const isSelected = selectedContacts.includes(contact.id);
                  return (
                    <tr 
                      key={contact.id} 
                      className={`hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors ${
                        isSelected ? 'bg-green-50 dark:bg-green-900/10' : ''
                      }`}
                    >
                      {!isAttendant && (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleContact(contact.id)}
                            className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500 cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                            {(contact.profile_name || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-slate-100 text-sm">
                              {contact.profile_name || t('contacts.noName')}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1">
                              <Phone size={11} />
                              {contact.wa_id}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {contact.status ? (
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusLabels[contact.status]?.color || 'bg-gray-100 text-gray-700'}`}>
                            {statusLabels[contact.status]?.label || contact.status}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-slate-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600 dark:text-slate-400">
                        {sourceLabels[contact.source] || contact.source || '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600 dark:text-slate-400">
                        {contact.assigned_to_name || <span className="text-gray-400 dark:text-slate-500">-</span>}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600 dark:text-slate-400">
                        {contact.campaign_name ? (
                          <span className="inline-flex items-center px-2.5 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-full text-xs font-medium">
                            {contact.campaign_name}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-slate-500">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {contact.opt_in ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
                            <CheckCircle2 size={12} />
                            {t('common.yes')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-xs font-medium">
                            <XCircle size={12} />
                            {t('common.no')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                          <Calendar size={14} className="text-gray-400 dark:text-slate-500" />
                          <span>{new Date(contact.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleViewContact(contact)}
                            className="p-2 text-gray-400 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title={t('contacts.viewDetails')}
                          >
                            <Eye size={18} />
                          </button>
                          {!isAttendant && (
                            <>
                              <button
                                onClick={() => openEditContactModal(contact)}
                                className="p-2 text-gray-400 dark:text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                                title="Editar"
                              >
                                <Edit2 size={18} />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedContacts([contact.id]);
                                  handleSendToBulk();
                                }}
                                className="p-2 text-gray-400 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                title={t('contacts.sendToCampaign')}
                              >
                                <Send size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteContact(contact)}
                                className="p-2 text-gray-400 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Excluir"
                              >
                                <Trash2 size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-3 bg-gray-50 dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-gray-500 dark:text-slate-400">
            {t('contacts.showing')} <strong className="text-gray-900 dark:text-slate-100">{paginatedContacts.length}</strong> {t('contacts.of')}{' '}
            <strong className="text-gray-900 dark:text-slate-100">{filtered.length}</strong> {t('contacts.contacts')}
            {selectedContacts.length > 0 && (
              <span className="text-green-600 dark:text-green-400 font-medium ml-2">
                ({t('contacts.selectedCount', { count: selectedContacts.length })})
              </span>
            )}
          </p>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-600 dark:text-slate-300 font-medium px-2">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-xl">
            <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 mb-4">
              {editingContact ? 'Editar Contato' : 'Novo Contato'}
            </h2>
            <form onSubmit={handleSaveContact} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Nome completo *
                  </label>
                  <input
                    type="text"
                    value={form.profile_name}
                    onChange={e => setForm({ ...form, profile_name: e.target.value })}
                    placeholder="Nome completo"
                    required
                    className="w-full p-2.5 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Telefone *
                  </label>
                  <input
                    type="text"
                    value={form.wa_id}
                    onChange={e => setForm({ ...form, wa_id: e.target.value })}
                    placeholder="Ex: 5511999998888"
                    required
                    disabled={!!editingContact}
                    className="w-full p-2.5 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none disabled:opacity-60"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="email@exemplo.com"
                    className="w-full p-2.5 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Origem
                  </label>
                  <select
                    value={form.source}
                    onChange={e => setForm({ ...form, source: e.target.value })}
                    className="w-full p-2.5 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                  >
                    <option value="manual">Manual</option>
                    <option value="website">Website</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="campaign">Campanha</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                    className="w-full p-2.5 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                  >
                    {Object.entries(statusLabels).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Setor
                  </label>
                  <select
                    value={form.sector_id}
                    onChange={e => setForm({ ...form, sector_id: e.target.value })}
                    className="w-full p-2.5 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                  >
                    <option value="">Sem setor</option>
                    {sectors.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Responsável
                </label>
                <select
                  value={form.assigned_to}
                  onChange={e => setForm({ ...form, assigned_to: e.target.value })}
                  className="w-full p-2.5 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                >
                  <option value="">Sem responsável</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-200 dark:border-slate-600 rounded-xl text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ⭐ NOVO: modal de exportação com filtros, estilo Meta Business Manager */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">Exportar contatos</h2>
              <button onClick={() => setShowExportModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Escolha os contatos que pretende exportar</p>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                    <input type="radio" checked={exportMode === 'all'} onChange={() => setExportMode('all')} className="text-green-600" />
                    Todos os contatos
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                    <input type="radio" checked={exportMode === 'filtered'} onChange={() => setExportMode('filtered')} className="text-green-600" />
                    Contatos filtrados
                  </label>
                </div>
              </div>

              {exportMode === 'filtered' && (
                <div className="space-y-2">
                  {exportFilters.map(f => (
                    <div key={f.key} className="flex items-center gap-2">
                      <select
                        value={f.field}
                        onChange={e => updateExportFilter(f.key, { field: e.target.value, value: defaultValueForField(e.target.value) })}
                        className="flex-1 px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                      >
                        <option value="origem">Origem</option>
                        <option value="sector_id">Setor</option>
                        <option value="status">Status</option>
                      </select>
                      <span className="text-xs text-gray-400 flex-shrink-0">Igual</span>
                      {f.field === 'origem' && (
                        <select
                          value={f.value}
                          onChange={e => updateExportFilter(f.key, { value: e.target.value })}
                          className="flex-1 px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                        >
                          <option value="ctwa">CTWA (Anúncio)</option>
                          <option value="outro">Outro</option>
                        </select>
                      )}
                      {f.field === 'sector_id' && (
                        <select
                          value={f.value}
                          onChange={e => updateExportFilter(f.key, { value: e.target.value })}
                          className="flex-1 px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                        >
                          {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      )}
                      {f.field === 'status' && (
                        <input
                          type="text"
                          value={f.value}
                          onChange={e => updateExportFilter(f.key, { value: e.target.value })}
                          className="flex-1 px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                          placeholder="Ex: new"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => removeExportFilter(f.key)}
                        className="text-red-400 hover:text-red-600 flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addExportFilter}
                    className="text-sm text-green-700 dark:text-green-400 font-medium hover:underline"
                  >
                    + Adicionar
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 p-5 border-t border-gray-100 dark:border-slate-700">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 border border-gray-200 dark:border-slate-600 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleApplyExport}
                disabled={exporting}
                className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50"
              >
                {exporting ? 'Exportando...' : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Contacts;
